import React from "react"
import { LedgerType } from "@digits-graphql/frontend/graphql-bearer"
import { svgPathStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgCheck } from "@digits-shared/components/SVGIcons/line/Check.svg"
import { SvgEdit01 } from "@digits-shared/components/SVGIcons/line/Edit01.svg"
import { SvgRefreshCw05 } from "@digits-shared/components/SVGIcons/line/RefreshCw05.svg"
import { SvgXClose } from "@digits-shared/components/SVGIcons/line/XClose.svg"
import { DigitsTooltip } from "@digits-shared/DesignSystem/Tooltip"
import colors from "@digits-shared/themes/colors"
import { AnimatePresence, m, type Variants } from "framer-motion"
import styled, { css } from "styled-components"

/*
  STYLES
*/

const variants: Variants = {
  entering: { opacity: 1, transition: { duration: 0.25 } },
  exiting: { opacity: 0, transition: { duration: 0.25 } },
}

const ActionsContainer = styled(m.div)`
  position: absolute;
  bottom: -8px;
  right: 18px;
  display: flex;
  gap: 4px;
`

const ActionButton = styled.button`
  border-radius: 50%;
  width: 16px;
  height: 16px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    opacity: 0.6;
  }
`

const AcceptButton = styled(ActionButton)`
  background-color: ${colors.primary};
`
const IgnoreButton = styled(ActionButton)`
  background-color: ${colors.secondary};
`

const CheckmarkIcon = styled(SvgCheck)`
  ${svgPathStyles(colors.white, 2)};
  width: 10px;
  height: 10px;
`

const IgnoreIcon = styled(SvgXClose)`
  ${svgPathStyles(colors.white, 2)};
  width: 10px;
  height: 10px;
`

const RetryIcon = styled(SvgRefreshCw05)`
  ${svgPathStyles(colors.white, 2)};
  width: 10px;
  height: 10px;
`

const EditIcon = styled(SvgEdit01)`
  ${svgPathStyles(colors.white, 2)};
  width: 8px;
  height: 8px;
`

const ButtonContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: scale 0.2s ease-in;
  z-index: 1;

  &:hover:not(:disabled) {
    transform: scale(1.2);
  }

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      pointer-events: none;
      opacity: 0.2;
    `}
`

/*
  INTERFACES
*/

interface ButtonTooltipPopOverProps {
  tooltip?: React.ReactNode
  disabled?: boolean
  className?: string
  children: React.ReactNode
}

/*
  COMPONENTS
*/

export const InputActions: React.FC<{
  onIgnore?: (event: React.MouseEvent) => void
  onAccept?: (event: React.MouseEvent) => void
  onEdit?: (event: React.MouseEvent) => void
  hasError?: boolean
  hasSuggestion?: boolean
  disabled?: boolean
  hideRetry?: boolean
  animationKey: string
  showActions: boolean
  ledgerType: LedgerType
}> = ({
  onIgnore,
  onAccept,
  disabled,
  hasError,
  hideRetry,
  animationKey,
  showActions,
  hasSuggestion,
  onEdit,
  ledgerType,
}) => (
  <AnimatePresence>
    {showActions && (
      <ActionsContainer
        key={animationKey}
        variants={variants}
        initial="exiting"
        animate="entering"
        exit="exiting"
      >
        {hasSuggestion ? (
          <ButtonTooltipWrapper tooltip="Edit" disabled={disabled}>
            <IgnoreButton onClick={onEdit} disabled={disabled}>
              <EditIcon />
            </IgnoreButton>
          </ButtonTooltipWrapper>
        ) : (
          <ButtonTooltipWrapper tooltip="Ignore" disabled={disabled}>
            <IgnoreButton onClick={onIgnore} disabled={disabled}>
              <IgnoreIcon />
            </IgnoreButton>
          </ButtonTooltipWrapper>
        )}

        {!hideRetry && (
          <ButtonTooltipWrapper
            tooltip={ledgerType === LedgerType.QuickBooks ? "Save to QBO" : "Save to ledger"}
            disabled={disabled}
          >
            <AcceptButton onClick={onAccept} disabled={disabled}>
              {hasError ? <RetryIcon /> : <CheckmarkIcon />}
            </AcceptButton>
          </ButtonTooltipWrapper>
        )}
      </ActionsContainer>
    )}
  </AnimatePresence>
)

export const ButtonTooltipWrapper = React.forwardRef<HTMLDivElement, ButtonTooltipPopOverProps>(
  ({ className, tooltip, disabled, children }, ref) => (
    <DigitsTooltip content={tooltip} disabled={disabled}>
      <ButtonContainer ref={ref} className={className} disabled={disabled}>
        {children}
      </ButtonContainer>
    </DigitsTooltip>
  )
)
